import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useSelector } from 'react-redux';
import { DatePicker, FieldGroup, TimePicker } from '@Sage/hoi-poi-ui';

import { formatDate, getDateFormatPlaceholder } from 'utils/dates';
import { getLiteral } from 'utils/getLiteral';

import './styles/dateTime.scss';

const TIME_INTERVAL_MINUTES = 5;

const DateTime = memo(
    ({
        autoFocus,
        className,
        error,
        isDisabled,
        isReadOnly,
        label,
        labelMode,
        onChange,
        placeholderReadOnly,
        readOnly,
        value,
    }) => {
        const lang = useSelector((state) => state.config?.userData?.langISOInterface);
        const [date, setDate] = useState(value ? new Date(value) : null);
        const [time, setTime] = useState(value ? new Date(value) : null);
        const userDateFormat = getDateFormatPlaceholder();
        const finalIsReadOnly = isReadOnly ?? readOnly ?? false;
        const finalIsDisabled = isDisabled ?? false;

        const parseTimeString = useCallback((timeStr) => {
            if (!timeStr) return { hours: 0, minutes: 0 };

            return {
                hours: timeStr.getHours(),
                minutes: timeStr.getMinutes(),
            };
        }, []);

        useEffect(() => {
            if (value) {
                const newDate = new Date(value);
                setDate(newDate);
                setTime(newDate);
            } else {
                setDate(null);
                setTime(null);
            }
        }, [value]);

        const getCombinedDateTime = useCallback(
            (newDate, newTime) => {
                if (!newDate || !newTime) return null;

                const combined = new Date(newDate);
                const { hours, minutes } = parseTimeString(newTime);

                combined.setHours(hours, minutes, 0, 0);

                return combined;
            },
            [parseTimeString],
        );

        const handleDateChange = useCallback(
            (newDate) => {
                setDate(newDate);
                if (newDate && time) {
                    const combinedDateTime = getCombinedDateTime(newDate, time);
                    onChange?.(combinedDateTime);
                } else if (!newDate) {
                    onChange?.(null);
                }
            },
            [getCombinedDateTime, onChange, time],
        );

        const handleTimeChange = useCallback(
            (newTime) => {
                setTime(newTime);
                if (date && newTime) {
                    const combinedDateTime = getCombinedDateTime(date, newTime);
                    onChange?.(combinedDateTime);
                } else if (!newTime) {
                    onChange?.(null);
                }
            },
            [getCombinedDateTime, onChange, date],
        );

        const fieldGroupProps = useMemo(
            () => ({
                inputs: [DatePicker, TimePicker],
                inputProps: [
                    {
                        autoFocus,
                        value: date,
                        onChange: handleDateChange,
                        lang,
                        calendarButtonLabel: getLiteral('label_today'),
                        placeholder: userDateFormat,
                        placeholderReadOnly: placeholderReadOnly || userDateFormat,
                        inputMaskFormat: userDateFormat,
                        formatDate: (dateVal) => formatDate(dateVal, 'L'),
                        isReadOnly: finalIsReadOnly,
                        isDisabled: finalIsDisabled,
                        className: 'fm-field-date-time__date-picker',
                    },
                    {
                        value: time,
                        onChange: handleTimeChange,
                        isReadOnly: finalIsReadOnly,
                        isDisabled: finalIsDisabled,
                        className: 'fm-field-date-time__time-picker',
                        placeholder: 'hh:mm',
                        placeholderReadOnly: placeholderReadOnly || 'hh:mm',
                        interval: TIME_INTERVAL_MINUTES,
                    },
                ],
            }),
            [
                autoFocus,
                date,
                finalIsDisabled,
                finalIsReadOnly,
                handleDateChange,
                handleTimeChange,
                lang,
                placeholderReadOnly,
                time,
                userDateFormat,
            ],
        );

        return (
            <div className={classnames('fm-field-date-time', className)}>
                <FieldGroup
                    label={label}
                    labelMode={labelMode}
                    error={error}
                    isFullWidth
                    {...fieldGroupProps}
                />
            </div>
        );
    },
);

DateTime.displayName = 'DateTime';

DateTime.propTypes = {
    autoFocus: PropTypes.bool,
    className: PropTypes.string,
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    isDisabled: PropTypes.bool,
    isReadOnly: PropTypes.bool,
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    labelMode: PropTypes.string,
    onChange: PropTypes.func,
    placeholderReadOnly: PropTypes.string,
    readOnly: PropTypes.bool,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
};

export default DateTime;
